import React from "react";

export default function NoData({
  title = "No data available",
  message = "We couldn't find anything to show here right now.",
  showButton = false,
  buttonText = "Try again",
  onRetry = null,
}) {
  const css = `
  :root{
    --bg-main: #05070d;
    --panel: rgba(255,255,255,0.06);
    --stroke: rgba(255,255,255,0.12);
    --accent-a: #22d3ee;
    --accent-b: #6366f1;
    --text-main: #ffffff;
    --text-muted: #b6c0d1;
  }

  *{box-sizing:border-box}

  .nodata-wrapper{
    min-height:100vh;
    display:flex;
    align-items:center;
    justify-content:center;
    padding:32px 20px;
    background:
      linear-gradient(120deg, rgba(34,211,238,0.08), transparent 40%),
      linear-gradient(300deg, rgba(99,102,241,0.10), transparent 45%),
      var(--bg-main);
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial;
    color: var(--text-main);
  }

  .nodata-panel{
    width:100%;
    max-width:720px;
    background: var(--panel);
    border:1px solid var(--stroke);
    border-radius:22px;
    padding:40px 28px;
    text-align:center;
    backdrop-filter: blur(14px);
    box-shadow:
      0 20px 60px rgba(0,0,0,0.6),
      inset 0 1px 0 rgba(255,255,255,0.05);
    position:relative;
    overflow:hidden;
  }

  .nodata-panel::before{
    content:"";
    position:absolute;
    inset:-1px;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255,255,255,0.06),
      transparent
    );
    animation: shimmer 8s linear infinite;
    pointer-events:none;
  }

  @keyframes shimmer{
    0%{transform:translateX(-100%)}
    100%{transform:translateX(100%)}
  }

  .icon-shell{
    width:96px;
    height:96px;
    margin:0 auto 18px;
    border-radius:50%;
    display:flex;
    align-items:center;
    justify-content:center;
    background:
      linear-gradient(145deg, rgba(255,255,255,0.14), rgba(255,255,255,0.02));
    border:1px solid var(--stroke);
    animation:pulse 3.2s ease-in-out infinite;
  }

  @keyframes pulse{
    0%{transform:scale(1)}
    50%{transform:scale(1.05)}
    100%{transform:scale(1)}
  }

  .icon-shell svg{
    width:44px;
    height:44px;
    stroke:url(#grad);
    opacity:0.95;
  }

  h1{
    margin:0;
    font-size:clamp(22px, 3vw, 30px);
    letter-spacing:-0.015em;
  }

  .nodata-text{
    margin-top:10px;
    font-size:clamp(14px, 1.8vw, 16px);
    color:var(--text-muted);
    line-height:1.6;
  }

  .nodata-actions{
    margin-top:26px;
    display:flex;
    justify-content:center;
  }

  .retry-btn{
    border:0;
    cursor:pointer;
    padding:12px 22px;
    font-weight:600;
    font-size:14px;
    border-radius:14px;
    color:#020617;
    background: linear-gradient(90deg, var(--accent-a), var(--accent-b));
    box-shadow:0 12px 28px rgba(0,0,0,0.55);
    transition:transform .18s ease, box-shadow .18s ease;
  }

  .retry-btn:hover{
    transform:translateY(-2px);
    box-shadow:0 18px 36px rgba(0,0,0,0.65);
  }

  .retry-btn:active{
    transform:translateY(0);
  }

  @media (max-width:520px){
    .nodata-panel{padding:32px 20px}
  }
  `;

  return (
    <section className="nodata-wrapper" aria-live="polite">
      <style>{css}</style>

      <div className="nodata-panel" role="region" aria-label="No data state">
        <div className="icon-shell" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
            <defs>
              <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#22d3ee"/>
                <stop offset="100%" stopColor="#6366f1"/>
              </linearGradient>
            </defs>
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12h8M12 8v8" />
          </svg>
        </div>

        <h1>{title}</h1>
        <p className="nodata-text">{message}</p>

        {showButton && (
          <div className="nodata-actions">
            <button
              className="retry-btn"
              onClick={onRetry}
              aria-label={buttonText}
            >
              {buttonText}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
